import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaGraduationCap, FaCalendarAlt, FaMapMarkerAlt, FaClock,
  FaUser, FaBriefcase, FaEnvelope, FaCheckCircle,
  FaChevronRight, FaChevronLeft, FaAward, FaLightbulb,
  FaBullseye, FaClipboardList, FaPaperPlane, FaStar,
  FaArrowRight, FaTimesCircle, FaShieldAlt, FaUsers,
  FaExclamationTriangle, FaQuoteLeft,
} from 'react-icons/fa';
import { getRecueilInfo, submitRecueil } from '../components/RecueilApi';
import Header from '../components/Header';
import Footer from '../components/Footer';

//  CONSTANTES 
const PRIORITES = [
  'Des échanges de pratiques',
  'Un perfectionnement',
  'Des nouveautés sur la fonction',
  'Des conseils individualisés',
  'Des rappels techniques',
];

const STEPS = [
  { id:1, label:'Accueil',   icon: FaClipboardList },
  { id:2, label:'Profil',    icon: FaUser          },
  { id:3, label:'Contexte',  icon: FaBriefcase     },
  { id:4, label:'Attentes',  icon: FaLightbulb     },
  { id:5, label:'Objectifs', icon: FaBullseye      },
  { id:6, label:'Soumettre', icon: FaPaperPlane    },
];

const INIT = {
  prenom:'', nom:'', fonction:'', formationInitiale:'',
  entite:'', telephone:'', email:'', missions:'',
  dejaFormeTheme:null, formationPrecedente:'', difficultes:'',
  demandePersonnellement:null, conseilPar:'',
  objectifPriseDePoste:null, casConcrets:null,
  priorites:[], autresPriorites:'',
  obj1:'', obj2:'', obj3:'',
  autresRemarques:'',
};

//  ÉCRANS D'ÉTAT 
const EcranChargement = () => (
  <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8f9fa', fontFamily:'Inter,sans-serif' }}>
    <div style={{ textAlign:'center' }}>
      <div style={{ width:52, height:52, border:'4px solid #fd7e14', borderTopColor:'transparent', borderRadius:'50%', animation:'spin .8s linear infinite', margin:'0 auto 18px' }} />
      <p style={{ fontSize:16, color:'#718096', fontWeight:600 }}>Chargement de votre formulaire...</p>
    </div>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const EcranErreur = ({ emoji, titre, texte }) => (
  <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f8f9fa', fontFamily:'Inter,sans-serif', padding:24 }}>
    <div style={{ textAlign:'center', maxWidth:480 }}>
      <div style={{ fontSize:72, marginBottom:20 }}>{emoji}</div>
      <h1 style={{ fontSize:28, fontWeight:900, color:'#1a202c', marginBottom:14 }}>{titre}</h1>
      <p style={{ fontSize:16, color:'#718096', lineHeight:1.8 }}>{texte}</p>
      <p style={{ marginTop:20, fontSize:14, color:'#a0aec0' }}>
        Contactez-nous : <a href="mailto:cinef.dev@gmail.com" style={{ color:'#fd7e14', textDecoration:'none', fontWeight:700 }}>cinef.dev@gmail.com</a>
      </p>
    </div>
  </div>
);

//  COMPOSANT PRINCIPAL 
const Recueil = () => {
  const { token } = useParams();

  const [sessionData, setSessionData] = useState(null);
  const [tokenError,  setTokenError]  = useState(null);
  const [step,        setStep]        = useState(1);
  const [form,        setForm]        = useState(INIT);
  const [errors,      setErrors]      = useState({});
  const [submitted,   setSubmitted]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [apiError,    setApiError]    = useState(null);
  const topRef = useRef(null);

  // Charger les infos du token
  useEffect(() => {
    if (!token) { setTokenError('INVALIDE'); return; }
    getRecueilInfo(token)
      .then(data => setSessionData(data))
      .catch(err => setTokenError(err.code || 'INVALIDE'));
  }, [token]);

  // Scroll haut à chaque étape
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior:'smooth', block:'start' });
  }, [step]);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => { const n={...p}; delete n[k]; return n; });
  };

  const togglePriorite = (p) => {
    setForm(prev => ({
      ...prev,
      priorites: prev.priorites.includes(p)
        ? prev.priorites.filter(x => x !== p)
        : [...prev.priorites, p],
    }));
    if (errors.priorites) setErrors(p => { const n={...p}; delete n.priorites; return n; });
  };

  const validate = (s) => {
    const e = {};
    if (s === 2) {
      if (!form.prenom.trim())   e.prenom   = 'Requis';
      if (!form.nom.trim())      e.nom      = 'Requis';
      if (!form.fonction.trim()) e.fonction = 'Requis';
      if (!form.entite.trim())   e.entite   = 'Requis';
      if (!form.email.trim())    e.email    = 'Requis';
      if (!form.missions.trim()) e.missions = 'Requis';
    }
    if (s === 3) {
      if (form.dejaFormeTheme         === null) e.dejaFormeTheme         = 'Répondez à cette question';
      if (form.demandePersonnellement === null) e.demandePersonnellement = 'Répondez à cette question';
      if (form.objectifPriseDePoste   === null) e.objectifPriseDePoste   = 'Répondez à cette question';
      if (form.casConcrets            === null) e.casConcrets            = 'Répondez à cette question';
    }
    if (s === 4 && form.priorites.length === 0) e.priorites = 'Sélectionnez au moins une priorité';
    if (s === 5) {
      if (!form.obj1.trim()) e.obj1 = 'Requis';
      if (!form.obj2.trim()) e.obj2 = 'Requis';
      if (!form.obj3.trim()) e.obj3 = 'Requis';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (step === 1 || validate(step)) setStep(s => Math.min(s+1, STEPS.length)); };
  const prev = () => setStep(s => Math.max(s-1, 1));

  const submit = async () => {
    if (!validate(step)) return;
    setSubmitting(true); setApiError(null);
    try {
      await submitRecueil(token, {
        nom:                      form.nom,
        prenom:                   form.prenom,
        fonction:                 form.fonction,
        formation_initiale:       form.formationInitiale,
        entite:                   form.entite,
        telephone:                form.telephone,
        email:                    form.email,
        missions:                 form.missions,
        deja_forme_theme:         form.dejaFormeTheme,
        formation_precedente:     form.formationPrecedente,
        demande_personnellement:  form.demandePersonnellement,
        difficultes:              form.difficultes,
        conseil_par:              form.conseilPar,
        objectif_prise_de_poste:  form.objectifPriseDePoste,
        cas_concrets:             form.casConcrets,
        priorites:                form.priorites,
        autres_priorites:         form.autresPriorites,
        objectif_1:               form.obj1,
        objectif_2:               form.obj2,
        objectif_3:               form.obj3,
        autres_remarques:         form.autresRemarques,
      });
      setSubmitted(true);
    } catch (err) {
      if (err.errors) {
        const mapped = {};
        Object.entries(err.errors).forEach(([k, msgs]) => {
          const fk = { formation_initiale:'formationInitiale', deja_forme_theme:'dejaFormeTheme', formation_precedente:'formationPrecedente', demande_personnellement:'demandePersonnellement', conseil_par:'conseilPar', objectif_prise_de_poste:'objectifPriseDePoste', cas_concrets:'casConcrets', autres_priorites:'autresPriorites', objectif_1:'obj1', objectif_2:'obj2', objectif_3:'obj3', autres_remarques:'autresRemarques' }[k] || k;
          mapped[fk] = Array.isArray(msgs) ? msgs[0] : msgs;
        });
        setErrors(mapped);
        setApiError('Veuillez corriger les erreurs ci-dessous.');
      } else {
        setApiError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const pct      = ((step-1) / (STEPS.length-1)) * 100;
  const SESSION  = sessionData?.session ?? {};

  //  Écrans d'état ─
  if (!token || tokenError === 'INVALIDE')  return <EcranErreur emoji="❌" titre="Lien invalide"       texte="Ce lien n'existe pas ou est incorrect. Vérifiez l'email que vous avez reçu." />;
  if (tokenError === 'EXPIRE')              return <EcranErreur emoji="⏰" titre="Lien expiré"         texte="Ce lien n'est plus valide. Contactez l'équipe CINEF Formation." />;
  if (tokenError === 'DEJA_REMPLI')         return <EcranErreur emoji="✅" titre="Recueil déjà soumis" texte="Vous avez déjà complété votre fiche. Merci !" />;
  if (!sessionData)                         return <EcranChargement />;

  //  Page confirmation ─
  if (submitted) return (
    <div style={{ fontFamily:'Inter,sans-serif', background:'#f8f9fa', minHeight:'100vh' }}>
      <Header />
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'75vh', padding:'60px 24px' }}>
        <div style={{ textAlign:'center', maxWidth:580, animation:'popIn .6s cubic-bezier(.34,1.56,.64,1)' }}>
          <div style={{ width:96, height:96, borderRadius:'50%', background:'linear-gradient(135deg,#198754,#20c997)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 28px', fontSize:42, color:'#fff', boxShadow:'0 16px 48px rgba(25,135,84,.28)' }}><FaCheckCircle /></div>
          <h1 style={{ fontSize:34, fontWeight:900, color:'#1a202c', marginBottom:14 }}>Recueil soumis avec succès !</h1>
          <p style={{ fontSize:16, color:'#718096', lineHeight:1.8, marginBottom:36 }}>
            Merci <strong style={{ color:'#1a202c' }}>{form.prenom} {form.nom}</strong>,<br />
            votre fiche a bien été transmise. Le formateur en prendra connaissance avant la session du <strong style={{ color:'#fd7e14' }}>{SESSION.dates}</strong>.
          </p>
          <a href="/" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'15px 34px', background:'linear-gradient(135deg,#fd7e14,#dc3545)', color:'#fff', borderRadius:12, textDecoration:'none', fontWeight:700, fontSize:16 }}>
            Retour à l'accueil <FaArrowRight />
          </a>
        </div>
      </div>
      <Footer />
      <style>{`@keyframes popIn{from{opacity:0;transform:scale(.86) translateY(20px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );

  //  Formulaire ─
  return (
    <div ref={topRef} style={{ fontFamily:'Inter,-apple-system,sans-serif', background:'#f8f9fa', minHeight:'100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        .rc-wrap{font-family:'Inter',-apple-system,sans-serif}
        .rc-cnt{max-width:1180px;margin:0 auto;padding:0 24px;margin-top:62px}
        .rc-prog{height:4px;background:#e2e8f0;position:sticky;top:0;z-index:200}
        .rc-prog-fill{height:100%;background:linear-gradient(90deg,#fd7e14,#198754);transition:width .7s cubic-bezier(.4,0,.2,1)}
        .rc-hero{background:#fff;padding:60px 0 48px;text-align:center;border-bottom:1px solid #e2e8f0}
        .rc-badge{display:inline-flex;align-items:center;gap:9px;background:#fff;padding:10px 24px;border-radius:50px;box-shadow:0 4px 20px rgba(0,0,0,.08);margin-bottom:22px;font-size:14px;font-weight:600;color:#fd7e14;border:1px solid rgba(253,126,20,.15)}
        .rc-h1{font-size:46px;font-weight:900;line-height:1.15;color:#1a202c;margin-bottom:18px;letter-spacing:-1.5px}
        .rc-grad{background:linear-gradient(135deg,#fd7e14,#dc3545,#198754);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
        .rc-desc{font-size:17px;color:#718096;line-height:1.75;max-width:580px;margin:0 auto 36px}
        .rc-fcard{max-width:760px;margin:0 auto;background:linear-gradient(135deg,#1a2535,#243447);border-radius:22px;padding:28px 32px;box-shadow:0 20px 60px rgba(0,0,0,.18);overflow:hidden;position:relative}
        .rc-fcard::before{content:'';position:absolute;right:-30px;top:-30px;width:160px;height:160px;border-radius:50%;background:radial-gradient(circle,rgba(253,126,20,.18),transparent);pointer-events:none}
        .rc-fmeta{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:18px}
        .rc-fmeta-item{display:flex;align-items:center;gap:9px;padding:10px 13px;border-radius:11px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.1)}
        .rc-stats{background:#fff;border-top:1px solid #e2e8f0;padding:20px 0}
        .rc-stats-inner{max-width:640px;margin:0 auto;display:grid;grid-template-columns:repeat(3,1fr);gap:0;padding:0 24px}
        .rc-stat{display:flex;align-items:center;gap:13px;padding:0 24px;border-right:1px solid #e2e8f0}
        .rc-stat:first-child{padding-left:0}
        .rc-stat:last-child{border-right:none}
        .rc-stepper-wrap{background:#fff;border-bottom:1px solid #e2e8f0;padding:20px 0;position:sticky;top:4px;z-index:150}
        .rc-stepper{display:flex;align-items:center;justify-content:center;max-width:720px;margin:0 auto;padding:0 24px}
        .rc-step{display:flex;flex-direction:column;align-items:center;gap:6px;flex:1;position:relative}
        .rc-step:not(:last-child)::after{content:'';position:absolute;top:17px;left:calc(50% + 17px);right:calc(-50% + 17px);height:2px;background:#e2e8f0;z-index:0;transition:background .4s}
        .rc-step.done:not(:last-child)::after,.rc-step.cur:not(:last-child)::after{background:#fd7e14}
        .rc-circle{width:34px;height:34px;border-radius:50%;border:2.5px solid;display:flex;align-items:center;justify-content:center;font-size:13px;z-index:1;background:#fff;transition:all .3s}
        .rc-slbl{font-size:11px;font-weight:700;transition:color .3s;white-space:nowrap}
        .rc-form-area{padding:52px 0 80px}
        .rc-card{max-width:740px;margin:0 auto;background:#fff;border-radius:22px;border:2px solid #e2e8f0;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,.05);animation:rcFade .35s ease}
        @keyframes rcFade{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
        .rc-card-head{padding:26px 32px;border-bottom:2px solid #f0f4f8;display:flex;align-items:center;gap:16px}
        .rc-hicon{width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
        .rc-grid{display:grid;grid-template-columns:1fr 1fr;gap:22px}
        .rc-field{display:flex;flex-direction:column;gap:7px}
        .rc-field.full{grid-column:1/-1}
        .rc-field label{font-size:13px;font-weight:700;color:#374151}
        .rc-field label span{color:#fd7e14;margin-left:3px}
        .rc-inp{height:50px;padding:0 16px;border:2px solid #e2e8f0;border-radius:12px;font-size:14.5px;font-family:inherit;color:#1a202c;background:#fff;outline:none;width:100%;transition:border-color .2s,box-shadow .2s}
        .rc-inp:focus{border-color:#fd7e14;box-shadow:0 0 0 4px rgba(253,126,20,.1)}
        .rc-inp.err{border-color:#dc3545}
        .rc-ta{padding:14px 16px;border:2px solid #e2e8f0;border-radius:12px;font-size:14.5px;font-family:inherit;color:#1a202c;background:#fff;outline:none;resize:vertical;width:100%;transition:border-color .2s}
        .rc-ta:focus{border-color:#fd7e14;box-shadow:0 0 0 4px rgba(253,126,20,.1)}
        .rc-ta.err{border-color:#dc3545}
        .rc-err{font-size:12px;color:#dc3545;font-weight:600}
        .rc-yn{display:flex;gap:12px}
        .rc-yn-btn{flex:1;height:48px;border-radius:12px;border:2px solid #e2e8f0;background:#fff;font-family:inherit;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;color:#718096}
        .rc-yn-btn.yes{border-color:#198754;background:rgba(25,135,84,.07);color:#198754}
        .rc-yn-btn.no{border-color:#dc3545;background:rgba(220,53,69,.07);color:#dc3545}
        .rc-slide{animation:rcSlide .22s ease}
        @keyframes rcSlide{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}
        .rc-info{display:flex;gap:13px;align-items:flex-start;padding:16px 18px;border-radius:12px;border:1px solid;font-size:14px;line-height:1.75;color:#4a5568}
        .rc-prio{display:flex;flex-direction:column;gap:10px;margin-bottom:24px}
        .rc-pitem{display:flex;align-items:center;gap:14px;padding:15px 18px;border-radius:14px;border:2px solid #e2e8f0;background:#fff;cursor:pointer;transition:all .2s;text-align:left;font-family:inherit;width:100%}
        .rc-pitem.on{border-color:#fd7e14;background:rgba(253,126,20,.05);box-shadow:0 2px 12px rgba(253,126,20,.1)}
        .rc-pbox{width:22px;height:22px;border-radius:6px;border:2px solid #e2e8f0;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;color:#fff;transition:all .18s}
        .rc-pitem.on .rc-pbox{border-color:#fd7e14;background:#fd7e14}
        .rc-plbl{font-size:15px;font-weight:500;color:#374151;flex:1}
        .rc-pitem.on .rc-plbl{font-weight:700;color:#fd7e14}
        .rc-obj-row{display:flex;gap:14px;align-items:flex-start}
        .rc-obj-num{width:40px;height:40px;border-radius:12px;background:linear-gradient(135deg,#fd7e14,#e8700a);color:#fff;font-size:16px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:5px;box-shadow:0 4px 12px rgba(253,126,20,.3)}
        .rc-sec-div{display:flex;align-items:center;gap:10px;margin:26px 0 18px;grid-column:1/-1}
        .rc-sec-line{flex:1;height:1px;background:#f0f4f8}
        .rc-sec-lbl{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#a0aec0;display:flex;align-items:center;gap:7px;white-space:nowrap}
        .rc-recap-card{background:#f8f9fa;border-radius:16px;border:2px solid #e2e8f0;overflow:hidden;margin-bottom:18px}
        .rc-recap-hd{display:flex;align-items:center;gap:10px;padding:13px 18px;border-bottom:1px solid #e2e8f0;background:#fff;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.7px;color:#374151}
        .rc-recap-body{padding:18px;display:grid;grid-template-columns:1fr 1fr;gap:16px}
        .rc-recap-lbl{font-size:10.5px;font-weight:700;text-transform:uppercase;color:#a0aec0}
        .rc-recap-val{font-size:14px;font-weight:600;color:#1a202c}
        .rc-nav{display:flex;align-items:center;justify-content:space-between;margin-top:36px;padding-top:26px;border-top:2px solid #f0f4f8}
        .rc-prev{display:flex;align-items:center;gap:9px;padding:13px 24px;border-radius:12px;border:2px solid #e2e8f0;background:#fff;font-family:inherit;font-size:15px;font-weight:700;color:#4a5568;cursor:pointer;transition:all .2s}
        .rc-prev:hover:not(:disabled){border-color:#cbd5e0;background:#f8f9fa}
        .rc-prev:disabled{opacity:.3;cursor:not-allowed}
        .rc-next{display:flex;align-items:center;gap:9px;padding:14px 30px;border-radius:12px;border:none;background:linear-gradient(135deg,#fd7e14,#e8700a);color:#fff;font-family:inherit;font-size:15px;font-weight:800;cursor:pointer;box-shadow:0 8px 24px rgba(253,126,20,.32);transition:all .2s}
        .rc-next:hover{transform:translateY(-3px);box-shadow:0 12px 32px rgba(253,126,20,.42)}
        .rc-next:disabled{opacity:.55;cursor:not-allowed;transform:none}
        .rc-submit{background:linear-gradient(135deg,#198754,#146c43)!important;box-shadow:0 8px 24px rgba(25,135,84,.32)!important}
        .rc-api-err{display:flex;align-items:center;gap:10px;padding:14px 18px;background:rgba(220,53,69,.06);border:1px solid rgba(220,53,69,.2);border-radius:12px;color:#dc3545;font-size:14px;font-weight:600;margin-bottom:20px}
        @media(max-width:640px){.rc-h1{font-size:30px}.rc-fmeta{grid-template-columns:1fr}.rc-grid{grid-template-columns:1fr}.rc-field.full{grid-column:1}.rc-slbl{display:none}.rc-recap-body{grid-template-columns:1fr}}
      `}</style>

      <Header />

      {/* Barre progression */}
      <div className="rc-prog"><div className="rc-prog-fill" style={{ width:`${pct}%` }} /></div>

      {/* HERO */}
      <section className="rc-hero">
        <div className="rc-cnt">
          <div className="rc-badge"><FaAward style={{ color:'#fd7e14' }} /> Recueil des Attentes Participants</div>
          <h1 className="rc-h1">Dites-nous tout sur<br /><span className="rc-grad">vos besoins et objectifs</span></h1>
          <p className="rc-desc">Avant chaque formation, CINEF prend le temps de comprendre vos attentes pour personnaliser la session à vos besoins réels.</p>

          {/* Carte session */}
          <div className="rc-fcard">
            <div style={{ display:'flex', alignItems:'flex-start', gap:14, marginBottom:4, position:'relative', zIndex:1 }}>
              <div style={{ width:48, height:48, borderRadius:13, background:'rgba(253,126,20,.18)', border:'1px solid rgba(253,126,20,.3)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fd7e14', fontSize:20, flexShrink:0 }}><FaGraduationCap /></div>
              <div>
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(255,255,255,.4)', marginBottom:5 }}>Formation concernée</div>
                <div style={{ fontSize:16, fontWeight:800, color:'#fff', lineHeight:1.4 }}>{SESSION.titre || '—'}</div>
              </div>
            </div>
            <div className="rc-fmeta" style={{ position:'relative', zIndex:1 }}>
              {[
                { icon:FaCalendarAlt, lbl:'Dates',  val:SESSION.dates },
                { icon:FaClock,       lbl:'Durée',  val:SESSION.duree },
                { icon:FaMapMarkerAlt,lbl:'Lieu',   val:SESSION.lieu  },
              ].filter(m => m.val).map((m, i) => {
                const Icon = m.icon;
                return (
                  <div key={i} className="rc-fmeta-item">
                    <Icon style={{ color:'#fd7e14', fontSize:13, flexShrink:0 }} />
                    <div>
                      <div style={{ fontSize:9, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'rgba(255,255,255,.38)', marginBottom:1 }}>{m.lbl}</div>
                      <div style={{ fontSize:13, fontWeight:600, color:'rgba(255,255,255,.9)' }}>{m.val}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <div className="rc-stats">
        <div className="rc-stats-inner">
          {[
            { icon:FaClock,     color:'#fd7e14', bg:'rgba(253,126,20,.1)', num:'~5 min',   lbl:'Temps estimé' },
            { icon:FaUsers,     color:'#0d6efd', bg:'rgba(13,110,253,.1)', num:'6 étapes', lbl:'À compléter'  },
            { icon:FaShieldAlt, color:'#198754', bg:'rgba(25,135,84,.1)',  num:'100%',     lbl:'Confidentiel' },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rc-stat">
                <div style={{ width:42, height:42, borderRadius:11, background:s.bg, color:s.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}><Icon /></div>
                <div>
                  <div style={{ fontSize:20, fontWeight:900, color:'#1a202c', lineHeight:1 }}>{s.num}</div>
                  <div style={{ fontSize:12, color:'#718096', fontWeight:600, marginTop:2 }}>{s.lbl}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* STEPPER */}
      <div className="rc-stepper-wrap">
        <div className="rc-stepper">
          {STEPS.map(s => {
            const Icon  = s.icon;
            const isDone = step > s.id;
            const isCur  = step === s.id;
            return (
              <div key={s.id} className={`rc-step${isDone?' done':''}${isCur?' cur':''}`}>
                <div className="rc-circle" style={{ borderColor: isCur?'#fd7e14':isDone?'#198754':'#e2e8f0', background: isCur?'#fd7e14':isDone?'#198754':'#fff', color: isCur||isDone?'#fff':'#a0aec0' }}>
                  {isDone ? <FaCheckCircle style={{ fontSize:14 }}/> : <Icon style={{ fontSize:13 }}/>}
                </div>
                <div className="rc-slbl" style={{ color: isCur?'#fd7e14':isDone?'#198754':'#a0aec0', fontWeight: isCur||isDone?700:500 }}>{s.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FORMULAIRE */}
      <section className="rc-form-area">
        <div className="rc-cnt">
          <div className="rc-card">

            {/* ÉTAPE 1 — Accueil */}
            {step===1 && (
              <div>
                <div className="rc-card-head">
                  <div className="rc-hicon" style={{ background:'rgba(253,126,20,.1)', color:'#fd7e14' }}><FaClipboardList /></div>
                  <div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#1a202c', marginBottom:3 }}>Bienvenue 👋</div>
                    <div style={{ fontSize:14, color:'#718096' }}>Avant de commencer, voici ce que vous allez remplir</div>
                  </div>
                </div>
                <div style={{ padding:32 }}>
                  <div className="rc-info" style={{ background:'rgba(253,126,20,.04)', borderColor:'rgba(253,126,20,.18)', marginBottom:28 }}>
                    <FaQuoteLeft style={{ color:'#fd7e14', fontSize:18, flexShrink:0, marginTop:2 }} />
                    <p><strong style={{ color:'#1a202c' }}>Ce recueil est strictement confidentiel.</strong> Il est utilisé exclusivement par notre formateur pour personnaliser le contenu de la session.</p>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                    {[
                      { icon:FaUser,      color:'#0d6efd', bg:'rgba(13,110,253,.1)',  t:'Votre profil',            d:'Identité, fonction, entité et missions'   },
                      { icon:FaBriefcase, color:'#8b5cf6', bg:'rgba(139,92,246,.1)',  t:'Contexte & parcours',     d:'Formations précédentes, motivations'      },
                      { icon:FaLightbulb, color:'#fd7e14', bg:'rgba(253,126,20,.1)',  t:'Attentes & priorités',    d:'Ce que vous souhaitez retirer'            },
                      { icon:FaBullseye,  color:'#198754', bg:'rgba(25,135,84,.1)',   t:'Objectifs opérationnels', d:'Vos 3 objectifs concrets'                 },
                    ].map((c, i) => {
                      const Icon = c.icon;
                      return (
                        <div key={i} style={{ padding:'18px 20px', borderRadius:14, border:'2px solid #e2e8f0', background:'#fff', display:'flex', gap:13 }}>
                          <div style={{ width:40, height:40, borderRadius:11, background:c.bg, color:c.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}><Icon /></div>
                          <div>
                            <div style={{ fontSize:14, fontWeight:800, color:'#1a202c', marginBottom:4 }}>{c.t}</div>
                            <div style={{ fontSize:13, color:'#718096', lineHeight:1.5 }}>{c.d}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ÉTAPE 2 — Profil */}
            {step===2 && (
              <div>
                <div className="rc-card-head">
                  <div className="rc-hicon" style={{ background:'rgba(13,110,253,.1)', color:'#0d6efd' }}><FaUser /></div>
                  <div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#1a202c', marginBottom:3 }}>Votre profil</div>
                    <div style={{ fontSize:14, color:'#718096' }}>Informations personnelles et professionnelles</div>
                  </div>
                </div>
                <div style={{ padding:32 }}>
                  <div className="rc-grid">
                    <div className="rc-sec-div"><div className="rc-sec-line"/><span className="rc-sec-lbl"><FaUser style={{ fontSize:10 }}/>Identité</span><div className="rc-sec-line"/></div>
                    <div className="rc-field"><label>Prénom <span>*</span></label><input className={`rc-inp${errors.prenom?' err':''}`} value={form.prenom} onChange={e=>set('prenom',e.target.value)} placeholder="Votre prénom"/>{errors.prenom&&<span className="rc-err">{errors.prenom}</span>}</div>
                    <div className="rc-field"><label>Nom <span>*</span></label><input className={`rc-inp${errors.nom?' err':''}`} value={form.nom} onChange={e=>set('nom',e.target.value)} placeholder="Votre nom"/>{errors.nom&&<span className="rc-err">{errors.nom}</span>}</div>
                    <div className="rc-sec-div"><div className="rc-sec-line"/><span className="rc-sec-lbl"><FaBriefcase style={{ fontSize:10 }}/>Situation professionnelle</span><div className="rc-sec-line"/></div>
                    <div className="rc-field"><label>Fonction / Poste <span>*</span></label><input className={`rc-inp${errors.fonction?' err':''}`} value={form.fonction} onChange={e=>set('fonction',e.target.value)} placeholder="ex : Assistante de Direction"/>{errors.fonction&&<span className="rc-err">{errors.fonction}</span>}</div>
                    <div className="rc-field"><label>Formation initiale</label><input className="rc-inp" value={form.formationInitiale} onChange={e=>set('formationInitiale',e.target.value)} placeholder="ex : BTS Secrétariat"/></div>
                    <div className="rc-field full"><label>Entité / Direction / Service <span>*</span></label><input className={`rc-inp${errors.entite?' err':''}`} value={form.entite} onChange={e=>set('entite',e.target.value)} placeholder="Nom de votre entreprise ou organisation"/>{errors.entite&&<span className="rc-err">{errors.entite}</span>}</div>
                    <div className="rc-sec-div"><div className="rc-sec-line"/><span className="rc-sec-lbl"><FaEnvelope style={{ fontSize:10 }}/>Coordonnées</span><div className="rc-sec-line"/></div>
                    <div className="rc-field"><label>Téléphone</label><input className="rc-inp" value={form.telephone} onChange={e=>set('telephone',e.target.value)} placeholder="+229 00 00 00 00" type="tel"/></div>
                    <div className="rc-field"><label>Email <span>*</span></label><input className={`rc-inp${errors.email?' err':''}`} value={form.email} onChange={e=>set('email',e.target.value)} placeholder="vous@entreprise.com" type="email"/>{errors.email&&<span className="rc-err">{errors.email}</span>}</div>
                    <div className="rc-sec-div"><div className="rc-sec-line"/><span className="rc-sec-lbl"><FaClipboardList style={{ fontSize:10 }}/>Missions</span><div className="rc-sec-line"/></div>
                    <div className="rc-field full"><label>Principales missions et activités <span>*</span></label><textarea className={`rc-ta${errors.missions?' err':''}`} value={form.missions} onChange={e=>set('missions',e.target.value)} placeholder="Décrivez brièvement vos responsabilités quotidiennes…" rows={4}/>{errors.missions&&<span className="rc-err">{errors.missions}</span>}</div>
                  </div>
                </div>
              </div>
            )}

            {/* ÉTAPE 3 — Contexte */}
            {step===3 && (
              <div>
                <div className="rc-card-head">
                  <div className="rc-hicon" style={{ background:'rgba(139,92,246,.1)', color:'#8b5cf6' }}><FaBriefcase /></div>
                  <div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#1a202c', marginBottom:3 }}>Contexte et parcours</div>
                    <div style={{ fontSize:14, color:'#718096' }}>Ces informations calibrent le niveau et l'approche pédagogique</div>
                  </div>
                </div>
                <div style={{ padding:32, display:'flex', flexDirection:'column', gap:24 }}>
                  <div className="rc-field">
                    <label>Avez-vous déjà suivi une formation sur ce thème ? <span>*</span></label>
                    <div className="rc-yn">
                      <button className={`rc-yn-btn${form.dejaFormeTheme===true?' yes':''}`} onClick={()=>set('dejaFormeTheme',true)}>{form.dejaFormeTheme===true&&<FaCheckCircle style={{ fontSize:13 }}/>}Oui</button>
                      <button className={`rc-yn-btn${form.dejaFormeTheme===false?' no':''}`} onClick={()=>set('dejaFormeTheme',false)}>{form.dejaFormeTheme===false&&<FaTimesCircle style={{ fontSize:13 }}/>}Non</button>
                    </div>
                    {errors.dejaFormeTheme&&<span className="rc-err">{errors.dejaFormeTheme}</span>}
                  </div>
                  {form.dejaFormeTheme===true&&(
                    <div className="rc-field rc-slide"><label>Laquelle et à quelle date ?</label><input className="rc-inp" value={form.formationPrecedente} onChange={e=>set('formationPrecedente',e.target.value)} placeholder="ex : Excel avancé — Juin 2023"/></div>
                  )}
                  <div className="rc-field">
                    <label>Avez-vous demandé personnellement à participer ? <span>*</span></label>
                    <div className="rc-yn">
                      <button className={`rc-yn-btn${form.demandePersonnellement===true?' yes':''}`} onClick={()=>set('demandePersonnellement',true)}>{form.demandePersonnellement===true&&<FaCheckCircle style={{ fontSize:13 }}/>}Oui</button>
                      <button className={`rc-yn-btn${form.demandePersonnellement===false?' no':''}`} onClick={()=>set('demandePersonnellement',false)}>{form.demandePersonnellement===false&&<FaTimesCircle style={{ fontSize:13 }}/>}Non</button>
                    </div>
                    {errors.demandePersonnellement&&<span className="rc-err">{errors.demandePersonnellement}</span>}
                  </div>
                  {form.demandePersonnellement===true&&(
                    <div className="rc-field rc-slide"><label>Quelles difficultés vous ont motivé(e) ?</label><textarea className="rc-ta" value={form.difficultes} onChange={e=>set('difficultes',e.target.value)} placeholder="Décrivez les difficultés ou lacunes…" rows={3}/></div>
                  )}
                  {form.demandePersonnellement===false&&(
                    <div className="rc-field rc-slide"><label>Conseillé(e) / envoyé(e) par qui ?</label><input className="rc-inp" value={form.conseilPar} onChange={e=>set('conseilPar',e.target.value)} placeholder="Nom et fonction de la personne"/></div>
                  )}
                  <div className="rc-field">
                    <label>Objectif de cette formation ? <span>*</span></label>
                    <div className="rc-yn">
                      <button className={`rc-yn-btn${form.objectifPriseDePoste===true?' yes':''}`} onClick={()=>set('objectifPriseDePoste',true)}>{form.objectifPriseDePoste===true&&<FaCheckCircle style={{ fontSize:13 }}/>}Prise de poste</button>
                      <button className={`rc-yn-btn${form.objectifPriseDePoste===false?' yes':''}`} onClick={()=>set('objectifPriseDePoste',false)}>{form.objectifPriseDePoste===false&&<FaCheckCircle style={{ fontSize:13 }}/>}Sensibilisation</button>
                    </div>
                    {errors.objectifPriseDePoste&&<span className="rc-err">{errors.objectifPriseDePoste}</span>}
                  </div>
                  <div className="rc-field">
                    <label>Apportez-vous des cas concrets ? <span>*</span></label>
                    <div className="rc-yn">
                      <button className={`rc-yn-btn${form.casConcrets===true?' yes':''}`} onClick={()=>set('casConcrets',true)}>{form.casConcrets===true&&<FaCheckCircle style={{ fontSize:13 }}/>}Oui</button>
                      <button className={`rc-yn-btn${form.casConcrets===false?' no':''}`} onClick={()=>set('casConcrets',false)}>{form.casConcrets===false&&<FaTimesCircle style={{ fontSize:13 }}/>}Non</button>
                    </div>
                    {errors.casConcrets&&<span className="rc-err">{errors.casConcrets}</span>}
                  </div>
                  {form.casConcrets===true&&(
                    <div className="rc-slide rc-info" style={{ background:'rgba(25,135,84,.05)', borderColor:'rgba(25,135,84,.18)' }}>
                      <FaCheckCircle style={{ color:'#198754', fontSize:15, flexShrink:0, marginTop:2 }}/>
                      <span style={{ fontWeight:600, color:'#198754' }}>Parfait ! Pensez à apporter vos fichiers le jour J.</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ÉTAPE 4 — Attentes */}
            {step===4 && (
              <div>
                <div className="rc-card-head">
                  <div className="rc-hicon" style={{ background:'rgba(253,126,20,.1)', color:'#fd7e14' }}><FaLightbulb /></div>
                  <div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#1a202c', marginBottom:3 }}>Attentes et priorités</div>
                    <div style={{ fontSize:14, color:'#718096' }}>Que souhaitez-vous avant tout retirer de cette formation ?</div>
                  </div>
                </div>
                <div style={{ padding:32 }}>
                  <p style={{ fontSize:14, color:'#718096', lineHeight:1.7, marginBottom:22 }}>Sélectionnez une ou plusieurs priorités :</p>
                  <div className="rc-prio">
                    {PRIORITES.map((p, i) => {
                      const on = form.priorites.includes(p);
                      return (
                        <button key={i} className={`rc-pitem${on?' on':''}`} onClick={()=>togglePriorite(p)}>
                          <div className="rc-pbox">{on&&<FaCheckCircle />}</div>
                          <span className="rc-plbl">{p}</span>
                          {on&&<span style={{ fontSize:11, fontWeight:800, color:'#fd7e14', background:'rgba(253,126,20,.1)', padding:'3px 10px', borderRadius:20 }}>Sélectionné</span>}
                        </button>
                      );
                    })}
                  </div>
                  {errors.priorites&&<span className="rc-err" style={{ display:'block', marginBottom:16 }}>{errors.priorites}</span>}
                  <div className="rc-field"><label>Autres attentes spécifiques</label><textarea className="rc-ta" value={form.autresPriorites} onChange={e=>set('autresPriorites',e.target.value)} placeholder="Précisez toute autre attente…" rows={3}/></div>
                </div>
              </div>
            )}

            {/* ÉTAPE 5 — Objectifs */}
            {step===5 && (
              <div>
                <div className="rc-card-head">
                  <div className="rc-hicon" style={{ background:'rgba(25,135,84,.1)', color:'#198754' }}><FaBullseye /></div>
                  <div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#1a202c', marginBottom:3 }}>Vos 3 objectifs opérationnels</div>
                    <div style={{ fontSize:14, color:'#718096' }}>Formalisez 3 objectifs concrets à atteindre à l'issue de la formation</div>
                  </div>
                </div>
                <div style={{ padding:32 }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:13, padding:'14px 18px', background:'rgba(25,135,84,.04)', border:'1px solid rgba(25,135,84,.15)', borderRadius:14, marginBottom:24, fontSize:14, color:'#4a5568', lineHeight:1.8 }}>
                    <FaStar style={{ color:'#198754', fontSize:17, flexShrink:0, marginTop:3 }}/>
                    <div>Un bon objectif est <strong>SMART</strong> : Spécifique, Mesurable, Atteignable, Réaliste et Temporel.<br/><em style={{ color:'#198754', fontWeight:600 }}>Ex : « Créer un tableau de bord Excel automatisé d'ici fin janvier 2026. »</em></div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                    {[{f:'obj1',ph:'Mon premier objectif opérationnel…'},{f:'obj2',ph:'Mon deuxième objectif opérationnel…'},{f:'obj3',ph:'Mon troisième objectif opérationnel…'}].map((o,i)=>(
                      <div key={i} className="rc-obj-row">
                        <div className="rc-obj-num">{i+1}</div>
                        <div style={{ flex:1 }}>
                          <input className={`rc-inp${errors[o.f]?' err':''}`} value={form[o.f]} onChange={e=>set(o.f,e.target.value)} placeholder={o.ph} style={{ height:52 }}/>
                          {errors[o.f]&&<span className="rc-err">{errors[o.f]}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ÉTAPE 6 — Soumettre */}
            {step===6 && (
              <div>
                <div className="rc-card-head">
                  <div className="rc-hicon" style={{ background:'rgba(32,201,151,.1)', color:'#20c997' }}><FaPaperPlane /></div>
                  <div>
                    <div style={{ fontSize:22, fontWeight:900, color:'#1a202c', marginBottom:3 }}>Remarques et soumission</div>
                    <div style={{ fontSize:14, color:'#718096' }}>Vérifiez votre récapitulatif puis soumettez</div>
                  </div>
                </div>
                <div style={{ padding:32 }}>
                  {apiError&&<div className="rc-api-err"><FaExclamationTriangle style={{ flexShrink:0 }}/>{apiError}</div>}
                  <div className="rc-field" style={{ marginBottom:28 }}>
                    <label>Autres remarques ou informations complémentaires</label>
                    <textarea className="rc-ta" value={form.autresRemarques} onChange={e=>set('autresRemarques',e.target.value)} placeholder="Contexte particulier, contraintes, accessibilité…" rows={4}/>
                  </div>
                  <div className="rc-recap-card">
                    <div className="rc-recap-hd"><FaClipboardList style={{ color:'#fd7e14', fontSize:14 }}/>Récapitulatif</div>
                    <div className="rc-recap-body">
                      {[
                        {l:'Participant',  v:`${form.prenom} ${form.nom}`.trim()||'—'},
                        {l:'Fonction',     v:form.fonction||'—'},
                        {l:'Entité',       v:form.entite||'—'},
                        {l:'Email',        v:form.email||'—'},
                        {l:'Déjà formé',   v:form.dejaFormeTheme===true?'Oui':form.dejaFormeTheme===false?'Non':'—'},
                        {l:'Cas concrets', v:form.casConcrets===true?'Oui':form.casConcrets===false?'Non':'—'},
                        {l:'Priorités',    v:form.priorites.length?`${form.priorites.length} sélectionnée(s)`:'—'},
                        {l:'Objectifs',    v:`${[form.obj1,form.obj2,form.obj3].filter(Boolean).length} défini(s)`},
                      ].map((d,i)=>(
                        <div key={i} style={{ display:'flex', flexDirection:'column', gap:3 }}>
                          <span className="rc-recap-lbl">{d.l}</span>
                          <span className="rc-recap-val">{d.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div style={{ padding:'0 32px 32px' }}>
              <div className="rc-nav">
                <button className="rc-prev" onClick={prev} disabled={step===1}><FaChevronLeft style={{ fontSize:12 }}/>Précédent</button>
                {step < STEPS.length ? (
                  <button className="rc-next" onClick={next}>{step===1?'Commencer':'Continuer'}<FaChevronRight style={{ fontSize:12 }}/></button>
                ) : (
                  <button className={`rc-next rc-submit`} onClick={submit} disabled={submitting}>
                    {submitting?'⏳ Envoi...':<><FaPaperPlane/>Soumettre mon recueil</>}
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Recueil;